import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell } from 'recharts';
import {
  Dumbbell, Utensils, User, Moon, BarChart3, Trophy, TrendingUp, Plus, Save, Loader2, Crown, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const CHART_TICK = { fill: '#71717a', fontSize: 10 };
const CHART_AXIS = { stroke: '#27272a' };
const CHART_TOOLTIP = { backgroundColor: '#111118', border: '1px solid #27272a', borderRadius: '2px', color: '#fff' };
const TABS = [
  { key: 'training', icon: Dumbbell, label: 'Entraînement', color: '#ef4444' },
  { key: 'nutrition', icon: Utensils, label: 'Nutrition', color: '#10b981' },
  { key: 'body', icon: User, label: 'Corps', color: '#3b82f6' },
  { key: 'recovery', icon: Moon, label: 'Récup.', color: '#f59e0b' },
  { key: 'global', icon: BarChart3, label: 'Global', color: '#FF6B35' },
  { key: 'rankings', icon: Crown, label: 'Classement', color: '#8b5cf6' },
];
const MACRO_COLORS = ['#ef4444', '#f59e0b', '#3b82f6'];

export default function DashboardPerfPage({ embedded = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('training');
  const [data, setData] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [rankSort, setRankSort] = useState('muscle_mass');
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logForm, setLogForm] = useState({
    weight_used: '', reps: '', sets: '', rest_time: '', session_duration: '', rpe: '',
    calories: '', protein: '', carbs: '', fat: '', hydration: '',
    weight: '', body_fat_pct: '', muscle_kg: '', arm_cm: '', waist_cm: '', thigh_cm: '',
    sleep_hours: '', sleep_quality: '', hrv: '', resting_hr: '', fatigue: ''
  });

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [dashRes, rankRes] = await Promise.all([
        apiClient.get(`/performance/dashboard/${user.id}`),
        apiClient.get('/performance/rankings')
      ]);
      setData(dashRes.data);
      setRankings(rankRes.data);
    } catch {
      toast.error('Erreur chargement dashboard');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sortedRankings = useMemo(() => {
    return [...rankings].sort((a, b) => {
      const asc = rankSort === 'body_fat';
      const va = a[rankSort] ?? 0;
      const vb = b[rankSort] ?? 0;
      return asc ? va - vb : vb - va;
    });
  }, [rankings, rankSort]);

  const handleLog = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(logForm).forEach(([k, v]) => {
        if (v !== '' && v !== null) payload[k] = parseFloat(v);
      });
      await apiClient.post('/performance/log', payload);
      toast.success('Données enregistrées !');
      setShowLog(false);
      setLogForm({
        weight_used: '', reps: '', sets: '', rest_time: '', session_duration: '', rpe: '',
        calories: '', protein: '', carbs: '', fat: '', hydration: '',
        weight: '', body_fat_pct: '', muscle_kg: '', arm_cm: '', waist_cm: '', thigh_cm: '',
        sleep_hours: '', sleep_quality: '', hrv: '', resting_hr: '', fatigue: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const t = data?.training || {};
  const n = data?.nutrition || {};
  const b = data?.body || {};
  const r = data?.recovery || {};
  const g = data?.global || {};

  return (
    <div className={`${embedded ? "" : "min-h-screen"} bg-[#0F0F0F] ${embedded ? "pb-4" : "pb-20"}`}>
      {!embedded && <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-xl font-bold text-white">Dashboard Perf.</h1>
            <p className="text-xs text-zinc-500">{user?.name}</p>
          </div>
          <Button onClick={() => setShowLog(!showLog)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid="log-perf-btn">
            <Plus className="w-4 h-4 mr-1" /> Logger
          </Button>
        </div>
        {/* Tab Bar */}
        <div className="flex px-2 pb-2 gap-1 overflow-x-auto" data-testid="perf-tabs">
          {TABS.map(tb => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-colors ${
                tab === tb.key ? 'text-black' : 'text-zinc-500 bg-[#1B1B1B] border border-[#2A2A2E]'
              }`}
              style={tab === tb.key ? { backgroundColor: tb.color } : {}}
              data-testid={`tab-${tb.key}`}
            >
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>
      </header>}

      <main className="p-4 space-y-4 animate-fade-in">
        {/* Log Form */}
        {showLog && (
          <div className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-sm p-4 space-y-4" data-testid="log-form">
            <h3 className="text-sm text-[#FF6B35] font-medium">Enregistrer mes données</h3>
            {/* Training */}
            <div>
              <p className="text-xs text-zinc-500 mb-2">Entraînement</p>
              <div className="grid grid-cols-3 gap-2">
                {[['weight_used','Charge (kg)'],['reps','Reps'],['sets','Séries'],['rest_time','Repos (s)'],['session_duration','Durée (min)'],['rpe','RPE (1-10)']].map(([k,l]) => (
                  <div key={k}><Label className="text-zinc-500 text-[10px]">{l}</Label><Input type="number" step="0.1" value={logForm[k]} onChange={e => setLogForm({...logForm, [k]: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm h-8" /></div>
                ))}
              </div>
            </div>
            {/* Nutrition */}
            <div>
              <p className="text-xs text-zinc-500 mb-2">Nutrition</p>
              <div className="grid grid-cols-5 gap-2">
                {[['calories','Cal'],['protein','Protéines'],['carbs','Glucides'],['fat','Lipides'],['hydration','Eau (L)']].map(([k,l]) => (
                  <div key={k}><Label className="text-zinc-500 text-[10px]">{l}</Label><Input type="number" step="0.1" value={logForm[k]} onChange={e => setLogForm({...logForm, [k]: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm h-8" /></div>
                ))}
              </div>
            </div>
            {/* Body & Recovery */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-zinc-500 mb-2">Corps</p>
                <div className="grid grid-cols-2 gap-2">
                  {[['weight','Poids'],['body_fat_pct','%MG'],['arm_cm','Bras cm'],['waist_cm','Taille cm']].map(([k,l]) => (
                    <div key={k}><Label className="text-zinc-500 text-[10px]">{l}</Label><Input type="number" step="0.1" value={logForm[k]} onChange={e => setLogForm({...logForm, [k]: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm h-8" /></div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-zinc-500 mb-2">Récupération</p>
                <div className="grid grid-cols-2 gap-2">
                  {[['sleep_hours','Sommeil h'],['sleep_quality','Qualité %'],['hrv','HRV'],['fatigue','Fatigue 1-10']].map(([k,l]) => (
                    <div key={k}><Label className="text-zinc-500 text-[10px]">{l}</Label><Input type="number" step="0.1" value={logForm[k]} onChange={e => setLogForm({...logForm, [k]: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm h-8" /></div>
                  ))}
                </div>
              </div>
            </div>
            <Button onClick={handleLog} disabled={saving} className="w-full bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="save-log">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
            </Button>
          </div>
        )}

        {/* TAB A - Training */}
        {tab === 'training' && (
          <div className="space-y-4" data-testid="tab-training-content">
            <div className="grid grid-cols-3 gap-2">
              <KpiCard label="Charge moy." value={`${t.avg_charge || 0}kg`} color="#ef4444" />
              <KpiCard label="Progression" value={`${t.charge_progression || 0}%`} color={t.charge_progression > 0 ? '#10b981' : '#ef4444'} />
              <KpiCard label="Densité" value={t.avg_density || 0} color="#f59e0b" />
              <KpiCard label="RPE moy." value={t.avg_rpe || 0} color="#3b82f6" />
              <KpiCard label="RPE > 8" value={`${t.high_rpe_pct || 0}%`} color="#ef4444" />
              <KpiCard label="Durée moy." value={`${t.avg_duration || 0}min`} color="#8b5cf6" />
            </div>
            {t.volume_weekly?.length > 0 && (
              <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3">
                <h4 className="text-xs text-zinc-500 mb-2">Volume total par séance</h4>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={t.volume_weekly.map((v, i) => ({ idx: i + 1, volume: v }))}>
                      <defs><linearGradient id="vg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} /><stop offset="95%" stopColor="#ef4444" stopOpacity={0} /></linearGradient></defs>
                      <XAxis dataKey="idx" tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} />
                      <YAxis tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} width={40} />
                      <Tooltip contentStyle={CHART_TOOLTIP} />
                      <Area type="monotone" dataKey="volume" stroke="#ef4444" fill="url(#vg)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB B - Nutrition */}
        {tab === 'nutrition' && (
          <div className="space-y-4" data-testid="tab-nutrition-content">
            <div className="grid grid-cols-2 gap-2">
              <KpiCard label="Cal. moyennes" value={`${n.avg_calories || 0}`} color="#10b981" />
              <KpiCard label="Protéines moy." value={`${n.avg_protein || 0}g`} color="#ef4444" />
              <KpiCard label="Prot/kg" value={`${n.protein_per_kg || 0}g/kg`} color="#f59e0b" />
              <KpiCard label="Repas loggés" value={n.meal_count || 0} color="#3b82f6" />
            </div>
            {n.avg_calories > 0 && (
              <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3">
                <h4 className="text-xs text-zinc-500 mb-2">Répartition macros</h4>
                <div className="h-40 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={[
                        { name: 'Protéines', value: n.avg_protein || 0 },
                        { name: 'Glucides', value: parseFloat(n.meals_data?.[0]?.carbs) || 30 },
                        { name: 'Lipides', value: parseFloat(n.meals_data?.[0]?.fat) || 15 }
                      ]} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value">
                        {MACRO_COLORS.map((c, i) => <Cell key={`macro-${c}`} fill={c} />)}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  {[['Protéines', '#ef4444'],['Glucides', '#f59e0b'],['Lipides', '#3b82f6']].map(([l,c]) => (
                    <span key={l} className="flex items-center gap-1 text-[10px] text-zinc-400"><span className="w-2 h-2 rounded-full" style={{backgroundColor:c}} />{l}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB C - Body */}
        {tab === 'body' && (
          <div className="space-y-4" data-testid="tab-body-content">
            <div className="grid grid-cols-3 gap-2">
              <KpiCard label="Var. poids" value={`${b.weight_change_weekly > 0 ? '+' : ''}${b.weight_change_weekly || 0}kg`} color={b.weight_change_weekly > 0 ? '#ef4444' : '#10b981'} />
              <KpiCard label="Masse maigre" value={`${b.lean_mass_ratio || 0}%`} color="#3b82f6" />
              <KpiCard label="Entrées" value={b.metrics_history?.length || 0} color="#8b5cf6" />
            </div>
            {b.metrics_history?.length > 1 && (
              <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3">
                <h4 className="text-xs text-zinc-500 mb-2">Composition corporelle</h4>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={b.metrics_history.map(m => ({
                      date: new Date(m.timestamp).toLocaleDateString('fr-FR', {day:'2-digit',month:'2-digit'}),
                      poids: m.weight,
                      muscle: m.muscle_mass,
                      graisse: m.body_fat
                    }))}>
                      <XAxis dataKey="date" tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} />
                      <YAxis tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} width={35} />
                      <Tooltip contentStyle={CHART_TOOLTIP} />
                      <Line type="monotone" dataKey="poids" stroke="#FF6B35" strokeWidth={2} dot={false} name="Poids" />
                      <Line type="monotone" dataKey="muscle" stroke="#3b82f6" strokeWidth={2} dot={false} name="Muscle" />
                      <Line type="monotone" dataKey="graisse" stroke="#ef4444" strokeWidth={2} dot={false} name="Graisse" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  {[['Poids','#FF6B35'],['Muscle','#3b82f6'],['Graisse','#ef4444']].map(([l,c]) => (
                    <span key={l} className="flex items-center gap-1 text-[10px] text-zinc-400"><span className="w-2 h-2 rounded-full" style={{backgroundColor:c}} />{l}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB D - Recovery */}
        {tab === 'recovery' && (
          <div className="space-y-4" data-testid="tab-recovery-content">
            <div className="grid grid-cols-3 gap-2">
              <KpiCard label="Sommeil" value={`${r.avg_sleep || 0}h`} color="#f59e0b" />
              <KpiCard label="Qualité" value={`${r.avg_sleep_quality || 0}%`} color="#10b981" />
              <KpiCard label="Dette sommeil" value={`${r.sleep_debt || 0}h`} color="#ef4444" />
            </div>
            {/* Recovery Score Gauge */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4 text-center">
              <p className="text-xs text-zinc-500 mb-3">Score récupération</p>
              <div className="relative w-32 h-32 mx-auto">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#27272a" strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none"
                    stroke={r.recovery_score >= 70 ? '#10b981' : (r.recovery_score >= 40 ? '#f59e0b' : '#ef4444')}
                    strokeWidth="6" strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - (r.recovery_score || 0) / 100)}`}
                    strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-mono text-3xl font-bold text-white">{r.recovery_score || 0}</span>
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">/100</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {r.avg_hrv != null && <KpiCard label="HRV moy." value={r.avg_hrv} color="#8b5cf6" />}
              {r.avg_fatigue != null && <KpiCard label="Fatigue moy." value={`${r.avg_fatigue}/10`} color="#ef4444" />}
            </div>
          </div>
        )}

        {/* TAB E - Global */}
        {tab === 'global' && (
          <div className="space-y-4" data-testid="tab-global-content">
            {/* Radar Chart */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3">
              <h4 className="text-xs text-zinc-500 mb-2">Radar 6 axes</h4>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={Object.entries(g.radar || {}).map(([key, val]) => ({
                    axis: key.charAt(0).toUpperCase() + key.slice(1),
                    value: val
                  }))}>
                    <PolarGrid stroke="#27272a" />
                    <PolarAngleAxis dataKey="axis" tick={CHART_TICK} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} />
                    <Radar name="Score" dataKey="value" stroke="#FF6B35" fill="#FF6B35" fillOpacity={0.2} strokeWidth={2} />
                    <Tooltip contentStyle={CHART_TOOLTIP} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Global Score */}
            <div className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-sm p-4 text-center">
              <p className="text-xs text-zinc-500 mb-1">Score global</p>
              <p className="font-mono text-5xl font-bold text-[#FF6B35]">{g.total_score || 0}</p>
              <p className="text-xs text-zinc-400">/100</p>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2">
              <KpiCard label="Jours présent" value={g.presence_days || 0} color="#FF6B35" />
              <KpiCard label="Challenges faits" value={g.challenges_done || 0} color="#f59e0b" />
              <KpiCard label="Semaines suivies" value={g.weeks_tracked || 0} color="#3b82f6" />
            </div>

            {/* Individual Radar Scores */}
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(g.radar || {}).map(([key, val]) => (
                <div key={key} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-2 text-center">
                  <p className="text-xs text-zinc-400 capitalize">{key}</p>
                  <p className="font-mono text-lg font-bold text-white">{val}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB F - Rankings */}
        {tab === 'rankings' && (
          <div className="space-y-4" data-testid="tab-rankings-content">
            {/* Sort selector */}
            <div className="flex gap-1 flex-wrap">
              {[
                { key: 'muscle_mass', label: 'Muscle' },
                { key: 'lean_ratio', label: '% Maigre' },
                { key: 'weight', label: 'Poids' },
                { key: 'body_fat', label: 'Graisse', asc: true },
                { key: 'avg_volume', label: 'Volume' },
                { key: 'max_charge', label: 'Charge max' },
                { key: 'avg_calories', label: 'Calories' },
                { key: 'presence_days', label: 'Présence' },
                { key: 'points', label: 'Points' },
                { key: 'challenges_done', label: 'Challenges' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => setRankSort(s.key)}
                  className={`px-2 py-1 rounded-sm text-[10px] font-medium transition-colors ${
                    rankSort === s.key ? 'bg-[#8b5cf6] text-white' : 'bg-[#1B1B1B] text-zinc-500 border border-[#2A2A2E]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Rankings Table */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden">
              <div className="divide-y divide-zinc-800">
                {sortedRankings
                  .map((member, idx) => {
                    const isMe = member.user_id === user?.id;
                    const rank = idx + 1;
                    const val = member[rankSort];
                    const RANK_UNITS = { weight: 'kg', muscle_mass: 'kg', body_fat: 'kg', max_charge: 'kg', lean_ratio: '%', avg_calories: 'kcal' };
                    const unit = RANK_UNITS[rankSort] || '';

                    return (
                      <div key={member.user_id} className={`flex items-center gap-3 p-3 ${isMe ? 'bg-[#FF6B35]/5' : ''}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          rank === 1 ? 'bg-yellow-500 text-black' :
                          rank === 2 ? 'bg-gray-400 text-black' :
                          rank === 3 ? 'bg-amber-600 text-black' :
                          'bg-zinc-800 text-zinc-500'
                        }`}>
                          {rank}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-zinc-400">
                          {member.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isMe ? 'text-[#FF6B35]' : 'text-white'}`}>
                            {member.name} {isMe ? '(moi)' : ''}
                          </p>
                          <div className="flex gap-2 text-xs text-zinc-400">
                            <span>{member.weight || '-'}kg</span>
                            <span>{member.muscle_mass || '-'}kg muscle</span>
                            <span>{member.sessions_count} séances</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-lg font-bold text-white">{val != null ? (typeof val === 'number' && val % 1 !== 0 ? val.toFixed(1) : val) : '-'}</p>
                          <p className="text-xs text-zinc-400">{unit}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!data && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-8 text-center">
            <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">Commence à logger tes données</p>
            <p className="text-xs text-zinc-500">Clique sur "Logger" pour enregistrer tes performances</p>
          </div>
        )}
      </main>
      {!embedded && <BottomNav />}
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3 text-center" style={{ borderTopColor: color, borderTopWidth: '2px' }}>
      <p className="font-mono text-lg font-bold text-white">{value}</p>
      <p className="text-xs text-zinc-400">{label}</p>
    </div>
  );
}
